import React, { useMemo, useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import numeral from 'numeraljs'
// import classNames from 'classnames'

import styles from '../component-styles/share-campaign-codes-button.module.scss'
import Button from './button'
import Modal, { ModalHead, ModalBody, ModalFooter } from './modal'
import Heading from './heading'
import ConfirmationButtons from './confirmation-buttons'
import Input, { Label } from './input'
import { FormRow, LabelSlot, FieldSlot } from './row'
import AddMultiValuesTags from './add-multi-values-tags'
import ToolTip from './tooltip'
import CampaignCodePreviewTable from './campaign-code-preview-table'
import {
  getUserListAsync,
  // getUserDetailsAsync,
  logActionAsync,
} from '../store/actions'
import { CampaignCodesState } from '../store/campaign-codes-reducer'
import { GraphQLState } from '../store/graphql-reducer'
import { SiteState } from '../store/site-reducer'
import { shareCodes } from '../api-client/graphql-client'

export function getShareEmails(shareEmails, typedValue): string[] {
  if (typedValue === '') {
    return shareEmails
  }
  if (shareEmails.length > 0) {
    if (shareEmails[shareEmails.length - 1] === typedValue) {
      return shareEmails
    }
    return [...shareEmails, ...[typedValue]]
  }
  return [typedValue]
}

interface Props {
  shareUserCodes?: boolean
  selectedCodes: string[]
  isActive?: boolean
  showSummaryTable: boolean
}

export default function ShareCampaignCodesButton({
  shareUserCodes = false,
  selectedCodes,
  isActive = true,
  showSummaryTable,
}: Props): React.ReactElement {
  const dispatch = useDispatch()

  const {
    graphql: { currentUser },
    site: { selected },
    campaignCodes: { generatedStructure, codes, usersCodes },
  }: {
    graphql: GraphQLState
    site: SiteState
    campaignCodes: CampaignCodesState
  } = useSelector((s: any): any => s)

  const { firstName, lastName } = currentUser || { firstName: '', lastName: '' }

  const [form, setForm] = useState(generatedStructure)

  const [previewCodes, setShowPreviewCodes] = useState<any>([])
  // const [submissionStatus, setSubmissionStatus] = useState<'' | 'success' | 'failed'>('')
  const [active, setActive] = useState(false)
  const [showIds, setShowIds] = useState(selectedCodes)
  const [shared, setShared] = useState(false)
  const [typedValue, setTypedValue] = useState('')
  const [shareEmails, setShareEmails] = useState<string[]>([])
  const [note, setNote] = useState('')
  const [subject, setSubject] = useState('')
  const [placeholder, setPlaceholder] = useState('')

  // const [mounted, setMounted] = useState(false)

  useEffect((): void => {
    setForm(generatedStructure)
    if (!shareUserCodes && codes.length > 0) {
      setShowPreviewCodes(codes)
    } else if (shareUserCodes && usersCodes.length > 0) {
      setShowPreviewCodes(usersCodes)
    }
    // eslint-disable-next-line
  }, [generatedStructure, codes, usersCodes])

  useEffect(() => {
    const hasList = selected && selected.permissionList
    if (!hasList) {
      dispatch(getUserListAsync())
    }
    // eslint-disable-next-line
  }, [])

  // useEffect(() => {
  //   dispatch(getUserDetailsAsync())
  //   // eslint-disable-next-line
  // }, [])

  useEffect(() => {
    if (!shareUserCodes && selectedCodes.length === 0 && codes.length > 0) {
      const sIds: string[] = []
      codes.forEach((c) => {
        if (c.cID) {
          sIds.push(c.cID)
        }
      })
      setShowIds(sIds)
    } else {
      setShowIds(selectedCodes)
    }
  }, [selectedCodes, shareUserCodes, codes])

  useEffect(() => {
    if (firstName !== '' && lastName !== '') {
      setPlaceholder(
        `${firstName} ${lastName} would like you to use the attached campaign codes.`,
      )
    }
    // eslint-disable-next-line
  }, [firstName, lastName])

  const userList = useMemo(() => {
    if (selected && selected.permissionList) {
      const usePending = selected.pendingUserList
        ? [...selected.pendingUserList]
        : []
      const res = [...selected.permissionList, ...usePending].map(
        (item) => item.email || '',
      )
      return res
    }
    return []
  }, [selected])

  let buttonText = 'Share'
  if (!shareUserCodes) {
    if (showIds.length === 0 || showIds.length === codes.length) {
      buttonText = `Share all`
    } else {
      buttonText = `Share selected (${numeral(showIds.length).format('0,0')})`
    }
  }

  return (
    <div>
      <Button
        disabled={!isActive}
        className={!shareUserCodes ? '' : styles.button}
        type={!shareUserCodes ? 'selectBox' : 'share'}
        align="right"
        onClick={() => {
          setActive(true)
          setTypedValue('')
          setSubject('')
          setNote('')
          setShareEmails([])
          setShared(false)
        }}
      >
        {buttonText}
      </Button>
      {shared && active && (
        <Modal
          type="white"
          narrow
          active={active}
          toggleActive={() => {
            setActive(false)
          }}
        >
          <ModalHead>
            <Heading type={3} align="left" className={styles.heading}>
              Share campaign link{showIds.length > 1 ? 's' : ''}
            </Heading>
          </ModalHead>
          <ModalBody>
            <p>
              Success! Your {numeral(showIds.length).format('0,0')} selected
              campaign link
              {showIds.length > 1 ? 's have' : ' has'} been sent.
            </p>
          </ModalBody>
          <ModalFooter>
            <ConfirmationButtons
              type="simple"
              className={styles.shareSingleButton}
              onYes={(): void => {
                setActive(false)
              }}
              yesLabel="OK"
            />
          </ModalFooter>
        </Modal>
      )}
      {!shared && active && (
        <Modal
          type="white"
          superWide={showSummaryTable}
          narrow={!showSummaryTable}
          active={active}
          toggleActive={() => {
            setActive(false)
          }}
        >
          <ModalHead>
            <Heading type={3} align="left" className={styles.heading}>
              Share campaign link{showIds.length > 1 ? 's' : ''}
            </Heading>
          </ModalHead>
          <ModalBody>
            <p>
              Send an email with an attached .csv file containing your{' '}
              {numeral(showIds.length).format('0,0')} selected campaign link
              {showIds.length > 1 ? 's' : ''}.
            </p>
            <div>
              {showSummaryTable && previewCodes.length > 0 && (
                <CampaignCodePreviewTable
                  showIds={showIds}
                  form={form}
                  codes={previewCodes}
                />
              )}
              <FormRow last>
                <LabelSlot noPadding column>
                  <Label id="share-with" modalHeading>
                    Share with
                  </Label>
                  <ToolTip
                    className={styles.tooltip}
                    message="Add multiple users by separating email addresses with commas."
                  />
                </LabelSlot>
                <FieldSlot noPadding column>
                  <AddMultiValuesTags
                    type="modal"
                    placeholder="Add email or find teammates"
                    possibleValues={userList}
                    enableCommaMultiItems
                    onKeyUp={(val: string) => {
                      setTypedValue(val)
                    }}
                    onChange={(val: string[]) => {
                      setShareEmails(val)
                    }}
                  />
                </FieldSlot>
              </FormRow>
              <FormRow last>
                <LabelSlot noPadding column>
                  <Label id="subject" modalHeading>
                    Email subject
                  </Label>
                  <ToolTip
                    className={styles.tooltip}
                    message="This is the subject line of the email."
                  />
                </LabelSlot>
                <FieldSlot noPadding column>
                  <Input
                    id="subject"
                    name="subject"
                    type="text"
                    value={subject}
                    placeholder="Campaign links"
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      const { value: val } = event.target
                      setSubject(val)
                    }}
                  />
                </FieldSlot>
              </FormRow>
              <FormRow last>
                <LabelSlot noPadding column>
                  <Label id="note" modalHeading>
                    Email note
                  </Label>
                  <ToolTip
                    className={styles.tooltip}
                    message="This text will be included in the email."
                  />
                </LabelSlot>
                <FieldSlot noPadding column>
                  <Input
                    id="note"
                    name="note"
                    type="textArea"
                    value={note}
                    placeholder={placeholder}
                    onChange={(val: string): void => {
                      setNote(val)
                    }}
                  />
                </FieldSlot>
              </FormRow>
            </div>
          </ModalBody>
          <ModalFooter>
            <ConfirmationButtons
              type="simple"
              onYes={(): void => {
                setShared(true)
                // eslint-disable-next-line
                ;(async function shareCodesAction() {
                  const useShareEmails = getShareEmails(
                    shareEmails,
                    typedValue,
                  ).filter((i) => i !== '' && i.indexOf('@') !== -1)
                  if (useShareEmails.length === 0) {
                    return
                  }
                  const data = {
                    subject: subject === '' ? 'Campaign links' : subject,
                    mergeStats: false,
                    emailList: useShareEmails,
                    codeList: [...showIds],
                    note,
                  }
                  await shareCodes(data)
                  const desc = {
                    users: useShareEmails.length,
                    codes: showIds.length,
                    note,
                    subject: data.subject,
                  }
                  const actionData = {
                    action: 'button-share-codes',
                    longDescription: JSON.stringify(desc),
                    websiteSection: 'track',
                  }
                  dispatch(logActionAsync(actionData))
                })()
              }}
              onNo={(): void => {
                setActive(false)
              }}
              disabled={
                getShareEmails(shareEmails, typedValue).filter((i) => i !== '')
                  .length === 0
              }
              yesLabel="Share"
              noLabel="Go back"
            />
          </ModalFooter>
        </Modal>
      )}
    </div>
  )
}
